import { Link, useLocation } from "wouter";
import { useCustomerInboxUnreadCount } from "@orbit/api-client-react";
import { useAuth } from "../hooks/useAuth";

interface NavItem {
  href: string;
  label: string;
  icon: (active: boolean) => JSX.Element;
}

const ITEMS: NavItem[] = [
  {
    href: "/cards",
    label: "Cards",
    icon: (active) => (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={active ? 2.4 : 1.8}
        className="h-6 w-6"
        aria-hidden="true"
      >
        <rect x="3" y="6" width="18" height="13" rx="2.5" />
        <path d="M3 11h18" />
      </svg>
    ),
  },
  {
    href: "/discover",
    label: "Discover",
    icon: (active) => (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={active ? 2.4 : 1.8}
        className="h-6 w-6"
        aria-hidden="true"
      >
        <circle cx="12" cy="12" r="9" />
        <path d="m15.5 8.5-2 5-5 2 2-5z" />
      </svg>
    ),
  },
  {
    href: "/inbox",
    label: "Inbox",
    icon: (active) => (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={active ? 2.4 : 1.8}
        className="h-6 w-6"
        aria-hidden="true"
      >
        <path d="M3 6.5A2.5 2.5 0 0 1 5.5 4h13A2.5 2.5 0 0 1 21 6.5V14H16l-2 3h-4l-2-3H3z" />
        <path d="M3 14v3.5A2.5 2.5 0 0 0 5.5 20h13a2.5 2.5 0 0 0 2.5-2.5V14" />
      </svg>
    ),
  },
  {
    href: "/profile",
    label: "Profile",
    icon: (active) => (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={active ? 2.4 : 1.8}
        className="h-6 w-6"
        aria-hidden="true"
      >
        <circle cx="12" cy="8" r="4" />
        <path d="M4 21c1.5-4 4.5-6 8-6s6.5 2 8 6" />
      </svg>
    ),
  },
];

export function BottomNav() {
  const [location] = useLocation();
  const { isAuthenticated } = useAuth();
  // Polling cadence matches the inbox spec: 60 seconds is frequent
  // enough that a freshly-arrived voucher reminder shows up before the
  // user finishes a typical session, without hammering the API.
  const unreadQ = useCustomerInboxUnreadCount({
    enabled: isAuthenticated,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
  const unread = unreadQ.data?.unreadCount ?? 0;
  return (
    <nav
      aria-label="Primary"
      className="fixed inset-x-0 bottom-0 z-30 border-t border-navy/10 bg-white/95 backdrop-blur"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      <ul className="mx-auto flex max-w-md items-stretch justify-around">
        {ITEMS.map((item) => {
          const active =
            location === item.href || location.startsWith(`${item.href}/`);
          const showBadge = item.href === "/inbox" && unread > 0;
          return (
            <li key={item.href} className="flex-1">
              <Link
                href={item.href}
                className={`flex flex-col items-center gap-0.5 py-2.5 text-xs ${
                  active ? "text-navy font-semibold" : "text-ink-dark"
                }`}
                aria-current={active ? "page" : undefined}
              >
                <span className="relative">
                  {item.icon(active)}
                  {showBadge && (
                    <span
                      className="absolute -right-1.5 -top-1 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-amber px-1 text-[10px] font-bold leading-none text-navy"
                      data-testid="badge-inbox-unread"
                      aria-label={`${unread} unread`}
                    >
                      {unread > 99 ? "99+" : unread}
                    </span>
                  )}
                </span>
                <span>{item.label}</span>
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
